import React, { useEffect, useState, useCallback } from "react";
import ClinicalValidationPlanForm from "../components/ClinicalValidationPlanForm";
import RichTextEditor from "../components/RichTextEditor";

// Super-admin Clients management. Lists clients (org-backed + standalone), create
// standalone clients, edit details (audited), invite a contact to register, and
// attach/download client documents. Calls /api/super-admin/clients/* (Bearer idToken).

const box = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16 };
const btn = { padding: "8px 14px", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 };
const primary = { ...btn, background: "#2563eb", color: "#fff" };
const subtle = { ...btn, background: "#e2e8f0", color: "#0f172a" };
const input = { width: "100%", padding: "8px", border: "1px solid #cbd5e1", borderRadius: 6, marginBottom: 8, boxSizing: "border-box" };
const th = { textAlign: "left", padding: "8px 10px", borderBottom: "2px solid #273d3b", background: "#273d3b", color: "#fff" };
const td = { padding: "8px 10px", borderBottom: "1px solid #e2e8f0" };
const lbl = { display: "block", fontSize: 12, fontWeight: 600, color: "#475569", margin: "8px 0 4px" };
const section = { background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 18 };

function getToken() {
    return localStorage.getItem("idToken");
}
function authHeaders(json = true) {
    const h = { Authorization: `Bearer ${getToken()}` };
    if (json) h["Content-Type"] = "application/json";
    return h;
}

export default function ClientsPage() {
    const [clients, setClients] = useState([]);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");
    const [flash, setFlash] = useState(null); // in-modal feedback: { kind:'success'|'error', text }
    const [showAdd, setShowAdd] = useState(false);
    const [addForm, setAddForm] = useState({ client_name: "", primary_contact_name: "", primary_contact_email: "", phone: "", status: "active", notes: "", address: { line1: "", line2: "", city: "", state: "", postal_code: "", country: "" } });
    const [detail, setDetail] = useState(null); // { client, users, documents, invitations, audit }
    const [detailTab, setDetailTab] = useState("details");
    const [editForm, setEditForm] = useState(null);
    const [inviteEmail, setInviteEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [proposals, setProposals] = useState([]);
    const [proposalsClient, setProposalsClient] = useState(null); // client whose proposals modal is open
    const [newProposalName, setNewProposalName] = useState("");
    const [prop, setProp] = useState(null); // { proposal, comments, attachments }
    const [propTab, setPropTab] = useState("details");
    const [propForm, setPropForm] = useState(null);
    const [commentText, setCommentText] = useState("");
    const [quotes, setQuotes] = useState([]);
    const [quotesClient, setQuotesClient] = useState(null); // client whose Quotes section is open
    const [clientQuotes, setClientQuotes] = useState([]);    // all quotes for that client (across proposals)
    const [newQuoteProposalId, setNewQuoteProposalId] = useState("");
    const [qe, setQe] = useState(null); // quote editor: { quote, versions, current }
    const [qItems, setQItems] = useState([]);
    const [qMeta, setQMeta] = useState({ tax_rate: 0, discount: 0, discount_type: "amount", currency: "USD", terms: "" });
    // Proposal documents (structured docs from the Resources library, e.g. Validation Plan)
    const [propDocs, setPropDocs] = useState([]);
    const [resourceLib, setResourceLib] = useState([]);
    const [attachResourceId, setAttachResourceId] = useState("");
    const [docEditor, setDocEditor] = useState(null); // { document, data }
    const [docBusy, setDocBusy] = useState(false);

    const loadClients = useCallback(async (q = "") => {
        setLoading(true);
        try {
            const res = await fetch(`/api/super-admin/clients${q ? `?query=${encodeURIComponent(q)}` : ""}`, { headers: authHeaders(), credentials: "include" });
            const data = await res.json();
            setClients(data.clients || []);
        } catch (e) {
            setMessage("Failed to load clients.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadClients(); }, [loadClients]);

    // Fetch client detail WITHOUT changing the active tab / clearing feedback.
    const refreshDetail = async (id) => {
        const res = await fetch(`/api/super-admin/clients/${id}`, { headers: authHeaders(), credentials: "include" });
        const data = await res.json();
        setDetail(data);
        let addr = {};
        try { addr = data.client.address_json ? (typeof data.client.address_json === "string" ? JSON.parse(data.client.address_json) : data.client.address_json) : {}; } catch (_) { addr = {}; }
        setEditForm({
            client_name: data.client.client_name || "",
            primary_contact_name: data.client.primary_contact_name || "",
            primary_contact_email: data.client.primary_contact_email || "",
            phone: data.client.phone || "",
            status: data.client.status || "active",
            notes: data.client.notes || "",
            address: { line1: addr.line1 || "", line2: addr.line2 || "", city: addr.city || "", state: addr.state || "", postal_code: addr.postal_code || "", country: addr.country || "" },
        });
        setInviteEmail(data.client.primary_contact_email || "");
    };
    const openDetail = async (id) => { setMessage(""); setFlash(null); setDetailTab("details"); await refreshDetail(id); };

    const loadProposals = async (clientId) => {
        const res = await fetch(`/api/super-admin/proposals?client_id=${clientId}`, { headers: authHeaders(), credentials: "include" });
        const data = await res.json();
        setProposals(data.proposals || []);
    };

    // Proposals are their own section, opened per-client from the list (not the details modal).
    const openProposals = (client) => { setFlash(null); setProposalsClient(client); loadProposals(client.id); };

    const createProposal = async () => {
        if (!newProposalName.trim()) { setFlash({ kind: "error", text: "Please enter a proposal name." }); return; }
        setBusy(true);
        try {
            const res = await fetch(`/api/super-admin/proposals`, { method: "POST", headers: authHeaders(), credentials: "include", body: JSON.stringify({ client_id: proposalsClient.id, name: newProposalName.trim() }) });
            if (!res.ok) throw new Error((await res.json()).error || "Failed");
            setNewProposalName("");
            setFlash({ kind: "success", text: "Proposal created." });
            loadProposals(proposalsClient.id);
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };

    // Fetch a proposal's data WITHOUT changing the active tab (used by in-modal refreshes).
    const refreshProposal = async (id) => {
        const res = await fetch(`/api/super-admin/proposals/${id}`, { headers: authHeaders(), credentials: "include" });
        const data = await res.json();
        setProp(data);
        loadQuotes(id);
        loadProposalDocs(id);
        setPropForm({
            name: data.proposal.name || "", status: data.proposal.status || "draft",
            nature_of_work: data.proposal.nature_of_work || "", description: data.proposal.description || "",
            data_type: data.proposal.data_type || "", sample_count: data.proposal.sample_count || "",
            reference_genome: data.proposal.reference_genome || "", deliverables: data.proposal.deliverables || "",
            deadline: (data.proposal.deadline || "").slice(0, 10), notes: data.proposal.notes || "",
        });
    };

    // Open from the list: reset to the Details tab.
    const openProposal = async (id) => { setFlash(null); setCommentText(""); setPropTab("details"); loadResourceLib(); await refreshProposal(id); };

    const saveProposal = async () => {
        setBusy(true);
        try {
            const res = await fetch(`/api/super-admin/proposals/${prop.proposal.id}`, { method: "PUT", headers: authHeaders(), credentials: "include", body: JSON.stringify(propForm) });
            if (!res.ok) throw new Error((await res.json()).error || "Failed");
            await refreshProposal(prop.proposal.id);
            if (proposalsClient) loadProposals(proposalsClient.id);
            setFlash({ kind: "success", text: "Proposal saved." });
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };

    const finalizeProposal = async () => {
        if (!window.confirm("Finalize this proposal? It will be marked final.")) return;
        setBusy(true);
        try {
            const res = await fetch(`/api/super-admin/proposals/${prop.proposal.id}/finalize`, { method: "POST", headers: authHeaders(), credentials: "include" });
            if (!res.ok) throw new Error((await res.json()).error || "Failed");
            await refreshProposal(prop.proposal.id);
            if (proposalsClient) loadProposals(proposalsClient.id);
            setFlash({ kind: "success", text: "Proposal finalized." });
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };

    const shareProposal = async () => {
        if (!window.confirm("Share this proposal and its quotes with the client by email?")) return;
        setBusy(true);
        try {
            const res = await fetch(`/api/super-admin/proposals/${prop.proposal.id}/share`, { method: "POST", headers: authHeaders(), credentials: "include" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed");
            await refreshProposal(prop.proposal.id);
            setFlash({ kind: "success", text: `Proposal shared with ${(data.sent_to || []).join(", ")}.` });
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };

    const generateProposalPdf = async () => {
        setBusy(true); setFlash(null);
        try {
            // Persist current edits first so the PDF reflects the latest content.
            const save = await fetch(`/api/super-admin/proposals/${prop.proposal.id}`, { method: "PUT", headers: authHeaders(), credentials: "include", body: JSON.stringify(propForm) });
            if (!save.ok) throw new Error((await save.json()).error || "Failed to save before PDF");
            const res = await fetch(`/api/super-admin/proposals/${prop.proposal.id}/pdf`, { method: "POST", headers: authHeaders(), credentials: "include" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed to generate PDF");
            if (data.url) window.open(data.url, "_blank", "noopener");
            setFlash({ kind: "success", text: "Proposal PDF generated." });
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };

    const addComment = async () => {
        if (!commentText.trim()) { setFlash({ kind: "error", text: "Enter a comment first." }); return; }
        setBusy(true);
        try {
            await fetch(`/api/super-admin/proposals/${prop.proposal.id}/comments`, { method: "POST", headers: authHeaders(), credentials: "include", body: JSON.stringify({ body: commentText.trim() }) });
            setCommentText("");
            await refreshProposal(prop.proposal.id);
            setFlash({ kind: "success", text: "Comment posted." });
        } catch (e) { setFlash({ kind: "error", text: "Failed to add comment." }); } finally { setBusy(false); }
    };

    const uploadProposalAttachment = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await fetch(`/api/super-admin/proposals/${prop.proposal.id}/attachments`, { method: "POST", headers: authHeaders(false), credentials: "include", body: fd });
            if (!res.ok) throw new Error((await res.json()).error || "Failed");
            await refreshProposal(prop.proposal.id);
            setFlash({ kind: "success", text: `Attached ${file.name}.` });
        } catch (e) { setFlash({ kind: "error", text: "Failed to upload attachment." }); } finally { setBusy(false); }
    };

    const downloadProposalAttachment = async (docId) => {
        const res = await fetch(`/api/super-admin/proposals/${prop.proposal.id}/attachments/${docId}/download`, { headers: authHeaders(), credentials: "include" });
        const data = await res.json();
        if (data.url) window.open(data.url, "_blank", "noopener");
    };

    // ---- Proposal documents (Resources library instances) ----
    const loadProposalDocs = async (proposalId) => {
        const res = await fetch(`/api/super-admin/proposals/${proposalId}/documents`, { headers: authHeaders(), credentials: "include" });
        const data = await res.json();
        setPropDocs(data.documents || []);
    };
    const loadResourceLib = async () => {
        try {
            const res = await fetch(`/api/super-admin/resources`, { headers: authHeaders(), credentials: "include" });
            const data = await res.json();
            setResourceLib(data.documents || []);
        } catch (_) { setResourceLib([]); }
    };
    const attachDocument = async () => {
        if (!attachResourceId) { setFlash({ kind: "error", text: "Choose a document from the library." }); return; }
        setDocBusy(true); setFlash(null);
        try {
            const res = await fetch(`/api/super-admin/proposals/${prop.proposal.id}/documents`, {
                method: "POST", headers: authHeaders(), credentials: "include", body: JSON.stringify({ resource_document_id: attachResourceId }),
            });
            if (!res.ok) throw new Error((await res.json()).error || "Failed");
            setAttachResourceId("");
            setFlash({ kind: "success", text: "Document attached." });
            await loadProposalDocs(prop.proposal.id);
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setDocBusy(false); }
    };
    const openDocument = async (docId) => {
        setFlash(null);
        const res = await fetch(`/api/super-admin/proposals/${prop.proposal.id}/documents/${docId}`, { headers: authHeaders(), credentials: "include" });
        const data = await res.json();
        setDocEditor({ document: data.document, data: data.document.data || {} });
    };
    const saveDocument = async (statusOverride) => {
        setDocBusy(true); setFlash(null);
        try {
            const body = { data: docEditor.data };
            if (statusOverride) body.status = statusOverride;
            const res = await fetch(`/api/super-admin/proposals/${prop.proposal.id}/documents/${docEditor.document.id}`, {
                method: "PUT", headers: authHeaders(), credentials: "include", body: JSON.stringify(body),
            });
            if (!res.ok) throw new Error((await res.json()).error || "Failed");
            setFlash({ kind: "success", text: statusOverride === "final" ? "Document finalized." : "Document saved." });
            await loadProposalDocs(prop.proposal.id);
            if (statusOverride) setDocEditor(null);
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setDocBusy(false); }
    };
    const generateDocPdf = async (docId) => {
        setDocBusy(true); setFlash(null);
        try {
            const res = await fetch(`/api/super-admin/proposals/${prop.proposal.id}/documents/${docId}/pdf`, {
                method: "POST", headers: authHeaders(), credentials: "include", body: "{}",
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed");
            setFlash({ kind: "success", text: "PDF generated." });
            if (data.url) window.open(data.url, "_blank", "noopener");
            await loadProposalDocs(prop.proposal.id);
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setDocBusy(false); }
    };
    const deleteDocument = async (docId) => {
        if (!window.confirm("Remove this document from the proposal?")) return;
        setDocBusy(true); setFlash(null);
        try {
            const res = await fetch(`/api/super-admin/proposals/${prop.proposal.id}/documents/${docId}`, { method: "DELETE", headers: authHeaders(), credentials: "include" });
            if (!res.ok) throw new Error((await res.json()).error || "Failed");
            await loadProposalDocs(prop.proposal.id);
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setDocBusy(false); }
    };

    // ---- Quotes ----
    const loadQuotes = async (proposalId) => {
        const res = await fetch(`/api/super-admin/quotes?proposal_id=${proposalId}`, { headers: authHeaders(), credentials: "include" });
        const data = await res.json();
        setQuotes(data.quotes || []);
    };
    // Client-level Quotes section (all quotes across the client's proposals).
    const loadClientQuotes = async (clientId) => {
        const res = await fetch(`/api/super-admin/quotes?client_id=${clientId}`, { headers: authHeaders(), credentials: "include" });
        const data = await res.json();
        setClientQuotes(data.quotes || []);
    };
    const openClientQuotes = async (client) => {
        setFlash(null); setQuotesClient(client); setNewQuoteProposalId("");
        await loadClientQuotes(client.id);
        await loadProposals(client.id); // for the "new quote" proposal picker
    };
    const refreshQuoteLists = async () => {
        if (prop) await loadQuotes(prop.proposal.id);
        if (quotesClient) await loadClientQuotes(quotesClient.id);
    };
    const createQuote = async (proposalId) => {
        if (!proposalId) { setFlash({ kind: "error", text: "Select a proposal for this quote." }); return; }
        setBusy(true);
        try {
            const res = await fetch(`/api/super-admin/quotes`, { method: "POST", headers: authHeaders(), credentials: "include", body: JSON.stringify({ proposal_id: proposalId }) });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed");
            await refreshQuoteLists();
            if (data.quote_id) openQuote(data.quote_id);
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };
    const openQuote = async (id) => {
        setFlash(null);
        const res = await fetch(`/api/super-admin/quotes/${id}`, { headers: authHeaders(), credentials: "include" });
        const data = await res.json();
        setQe(data);
        const cur = data.current || {};
        setQItems((cur.items || []).map((it) => ({ title: it.title || "", description: it.description || "", quantity: it.quantity, unit: it.unit || "", unit_price: it.unit_price, taxable: it.taxable !== 0, notes: it.notes || "" })));
        setQMeta({ tax_rate: cur.tax_rate || 0, discount: (cur.discount_value ?? cur.discount) || 0, discount_type: cur.discount_type || "amount", currency: cur.currency || "USD", terms: cur.terms || "" });
    };
    const addRow = () => setQItems([...qItems, { title: "", description: "", quantity: 1, unit: "", unit_price: 0, taxable: true, notes: "" }]);
    const updateRow = (i, field, val) => setQItems(qItems.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
    const removeRow = (i) => setQItems(qItems.filter((_, idx) => idx !== i));
    // Reorder a line item by swapping it with its neighbour. Row order IS the saved order
    // (the server assigns row_no by array index on save).
    const moveRow = (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= qItems.length) return;
        const next = qItems.slice();
        [next[i], next[j]] = [next[j], next[i]];
        setQItems(next);
    };
    const saveQuote = async () => {
        setBusy(true);
        try {
            const res = await fetch(`/api/super-admin/quotes/${qe.quote.id}`, {
                method: "PUT", headers: authHeaders(), credentials: "include",
                body: JSON.stringify({ items: qItems, tax_rate: Number(qMeta.tax_rate) || 0, discount_value: Number(qMeta.discount) || 0, discount_type: qMeta.discount_type || "amount", currency: qMeta.currency, terms: qMeta.terms }),
            });
            if (!res.ok) throw new Error((await res.json()).error || "Failed");
            await openQuote(qe.quote.id);
            await refreshQuoteLists();
            setFlash({ kind: "success", text: "Quote saved." });
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };
    const generateQuotePdf = async () => {
        setBusy(true);
        try {
            const res = await fetch(`/api/super-admin/quotes/${qe.quote.id}/generate-pdf`, { method: "POST", headers: authHeaders(), credentials: "include" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed");
            if (data.url) window.open(data.url, "_blank", "noopener");
            await openQuote(qe.quote.id);
            setFlash({ kind: "success", text: "PDF generated." });
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };

    const sendQuote = async () => {
        if (!window.confirm("Send this quote to the client? The current version becomes the sent version.")) return;
        setBusy(true);
        try {
            const res = await fetch(`/api/super-admin/quotes/${qe.quote.id}/send`, { method: "POST", headers: authHeaders(), credentials: "include" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed");
            setFlash({ kind: "success", text: `Quote sent to ${(data.sent_to || []).join(", ")}${data.pdf ? " (with PDF)" : " (PDF pending — server libs)"}.` });
            await openQuote(qe.quote.id);
            await refreshQuoteLists();
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };
    const downloadContract = async () => {
        const res = await fetch(`/api/super-admin/quotes/${qe.quote.id}/contract`, { headers: authHeaders(), credentials: "include" });
        const data = await res.json();
        if (data.url) window.open(data.url, "_blank", "noopener"); else setFlash({ kind: "error", text: data.error || "No contract available." });
    };

    // Client-side preview of totals (server recomputes authoritatively on save).
    const previewTotals = () => {
        let subtotal = 0, taxable = 0;
        qItems.forEach((it) => { const amt = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0); subtotal += amt; if (it.taxable) taxable += amt; });
        // Mirror the server: discount (fixed amount or % of pre-tax subtotal) applies to the
        // pre-tax total; tax is charged on the taxable base after discount.
        const dv = Math.max(Number(qMeta.discount) || 0, 0);
        const rawDisc = qMeta.discount_type === "percent" ? (subtotal * dv / 100) : dv;
        const disc = Math.min(rawDisc, subtotal);
        const rate = Number(qMeta.tax_rate) || 0;
        const taxableAfterDiscount = subtotal > 0 ? taxable * ((subtotal - disc) / subtotal) : 0;
        const tax = taxableAfterDiscount * rate / 100;
        const total = subtotal - disc + tax;
        return { subtotal, tax, total, discount: disc };
    };

    const createClient = async () => {
        if (!addForm.client_name.trim()) { setFlash({ kind: "error", text: "Client name is required." }); return; }
        setBusy(true);
        try {
            const res = await fetch(`/api/super-admin/clients`, { method: "POST", headers: authHeaders(), credentials: "include", body: JSON.stringify({ ...addForm, address_json: JSON.stringify(addForm.address || {}) }) });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed");
            // Auto-send a registration invitation when a contact email was provided, so the
            // client can register on the portal to see proposals/quotes and comment.
            const email = (addForm.primary_contact_email || "").trim();
            let invited = false;
            if (email && data.client && data.client.id) {
                const inv = await fetch(`/api/super-admin/clients/${data.client.id}/invite`, { method: "POST", headers: authHeaders(), credentials: "include", body: JSON.stringify({ invitee_email: email }) });
                invited = inv.ok;
            }
            setShowAdd(false); setFlash(null);
            setAddForm({ client_name: "", primary_contact_name: "", primary_contact_email: "", phone: "", status: "active", notes: "", address: { line1: "", line2: "", city: "", state: "", postal_code: "", country: "" } });
            let msg = "Client created.";
            if (email) msg += invited ? ` Registration invite emailed to ${email}.` : " But the invite email could not be sent — retry from the client's Access tab.";
            else msg += " Add a contact email and invite them from the Access tab so they can register.";
            setMessage(msg);
            loadClients(query);
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };

    const saveEdit = async () => {
        setBusy(true);
        try {
            const res = await fetch(`/api/super-admin/clients/${detail.client.id}`, {
                method: "PUT", headers: authHeaders(), credentials: "include",
                body: JSON.stringify({
                    client_name: editForm.client_name, primary_contact_name: editForm.primary_contact_name,
                    primary_contact_email: editForm.primary_contact_email, phone: editForm.phone,
                    status: editForm.status, notes: editForm.notes,
                    address_json: JSON.stringify(editForm.address || {}), reason: "admin edit",
                }),
            });
            if (!res.ok) throw new Error((await res.json()).error || "Failed");
            await refreshDetail(detail.client.id);
            loadClients(query);
            setFlash({ kind: "success", text: "Client details saved." });
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };

    const sendInvite = async () => {
        const email = (inviteEmail || "").trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setFlash({ kind: "error", text: "Enter a valid email address." }); return; }
        setBusy(true);
        try {
            const res = await fetch(`/api/super-admin/clients/${detail.client.id}/invite`, { method: "POST", headers: authHeaders(), credentials: "include", body: JSON.stringify({ invitee_email: email }) });
            if (!res.ok) throw new Error((await res.json()).error || "Failed");
            await refreshDetail(detail.client.id);
            setFlash({ kind: "success", text: `Registration invite sent to ${email}.` });
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };

    const uploadDoc = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await fetch(`/api/super-admin/clients/${detail.client.id}/documents`, { method: "POST", headers: authHeaders(false), credentials: "include", body: fd });
            if (!res.ok) throw new Error((await res.json()).error || "Failed");
            await refreshDetail(detail.client.id);
            setFlash({ kind: "success", text: `Uploaded ${file.name}.` });
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };

    const downloadDoc = async (docId) => {
        const res = await fetch(`/api/super-admin/clients/${detail.client.id}/documents/${docId}/download`, { headers: authHeaders(), credentials: "include" });
        const data = await res.json();
        if (data.url) window.open(data.url, "_blank", "noopener");
    };

    // ---- Deletes (hard delete with cascade + confirmation) ----
    const deleteClient = async (id) => {
        if (!window.confirm("Delete this client and ALL its proposals, quotes, documents and logins? This cannot be undone.")) return;
        setBusy(true);
        try {
            const res = await fetch(`/api/super-admin/clients/${id}`, { method: "DELETE", headers: authHeaders(), credentials: "include" });
            if (!res.ok) throw new Error((await res.json()).error || "Failed");
            setDetail(null); setProposalsClient(null); setQuotesClient(null);
            setMessage("Client deleted.");
            loadClients(query);
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };
    const deleteProposal = async (id) => {
        if (!window.confirm("Delete this proposal and its quotes, comments and attachments? This cannot be undone.")) return;
        setBusy(true);
        try {
            const res = await fetch(`/api/super-admin/proposals/${id}`, { method: "DELETE", headers: authHeaders(), credentials: "include" });
            if (!res.ok) throw new Error((await res.json()).error || "Failed");
            setProp(null);
            if (proposalsClient) loadProposals(proposalsClient.id);
            if (quotesClient) { loadClientQuotes(quotesClient.id); loadProposals(quotesClient.id); }
            setFlash({ kind: "success", text: "Proposal deleted." });
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };
    const deleteQuote = async (id) => {
        if (!window.confirm("Delete this quote and all its versions? This cannot be undone.")) return;
        setBusy(true);
        try {
            const res = await fetch(`/api/super-admin/quotes/${id}`, { method: "DELETE", headers: authHeaders(), credentials: "include" });
            if (!res.ok) throw new Error((await res.json()).error || "Failed");
            setQe(null);
            await refreshQuoteLists();
            setFlash({ kind: "success", text: "Quote deleted." });
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };

    return (
        <div style={{ padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h2 style={{ margin: 0 }}>Clients</h2>
                <button style={primary} onClick={() => { setFlash(null); setShowAdd(true); }}>+ Add Client</button>
            </div>

            {message && <div style={{ ...box, background: "#eff6ff", borderColor: "#bfdbfe", marginBottom: 12 }}>{message}</div>}

            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <input style={{ ...input, marginBottom: 0, maxWidth: 360 }} placeholder="Search by name or email…" value={query}
                    onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadClients(query)} />
                <button style={subtle} onClick={() => loadClients(query)}>Search</button>
            </div>

            <div style={box}>
                {loading ? <p>Loading…</p> : (
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead><tr>
                            <th style={th}>Client</th><th style={th}>Type</th><th style={th}>Contact</th>
                            <th style={th}>Status</th><th style={th}>Users</th><th style={th}>Docs</th><th style={th}></th>
                        </tr></thead>
                        <tbody>
                            {clients.map((c) => {
                                // Proposals/Quotes can be prepared for ANY client — including those
                                // still pending registration. A client only SEES a proposal in their
                                // portal once it's registered AND the proposal status is 'released'.
                                const isRegistered = c.client_type === "organization" || c.registered_users > 0;
                                return (
                                <tr key={c.id}>
                                    <td style={td}>{c.client_name}</td>
                                    <td style={td}>{c.client_type}</td>
                                    <td style={td}>{c.primary_contact_email || "—"}</td>
                                    <td style={td}>{c.status}</td>
                                    <td style={td}>{c.registered_users}{c.pending_invites ? ` (+${c.pending_invites} invited)` : ""}</td>
                                    <td style={td}>{c.document_count}</td>
                                    <td style={td}>
                                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                                            <button style={subtle} onClick={() => openDetail(c.id)}>Manage details</button>
                                            <button style={{ ...subtle, background: "#dbeafe" }} onClick={() => openProposals(c)}>Proposals</button>
                                            <button style={{ ...subtle, background: "#dcfce7" }} onClick={() => openClientQuotes(c)}>Quotes</button>
                                            {!isRegistered && (
                                                <span title="This client hasn't completed registration yet. You can prepare proposals/quotes now; they'll appear in the client's portal once registered and the proposal is Released." style={{ fontSize: 12, fontWeight: 600, color: "#b45309", background: "#fef3c7", padding: "4px 10px", borderRadius: 999 }}>Pending Registration</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                );
                            })}
                            {!clients.length && <tr><td style={td} colSpan={7}>No clients found.</td></tr>}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Add-client modal */}
            {showAdd && (
                <Modal title="Add Client" subtitle="Create a standalone client. If a contact email is provided, a registration invite is emailed automatically." onClose={() => setShowAdd(false)}>
                    <Flash flash={flash} />
                    <div style={{ ...section, maxWidth: 760 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
                            <div><label style={lbl}>Client name *</label><input style={input} value={addForm.client_name} onChange={(e) => setAddForm({ ...addForm, client_name: e.target.value })} /></div>
                            <div><label style={lbl}>Status</label>
                                <select style={input} value={addForm.status} onChange={(e) => setAddForm({ ...addForm, status: e.target.value })}>
                                    <option value="prospect">prospect</option><option value="active">active</option><option value="inactive">inactive</option>
                                </select></div>
                            <div><label style={lbl}>Primary contact name</label><input style={input} value={addForm.primary_contact_name} onChange={(e) => setAddForm({ ...addForm, primary_contact_name: e.target.value })} /></div>
                            <div><label style={lbl}>Primary contact email</label><input style={input} value={addForm.primary_contact_email} onChange={(e) => setAddForm({ ...addForm, primary_contact_email: e.target.value })} /></div>
                            <div><label style={lbl}>Phone</label><input style={input} value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} /></div>
                        </div>
                        <div style={{ fontWeight: 600, color: "#475569", fontSize: 13, marginTop: 8 }}>Address</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
                            <div><label style={lbl}>Address line 1</label><input style={input} value={addForm.address.line1} onChange={(e) => setAddForm({ ...addForm, address: { ...addForm.address, line1: e.target.value } })} /></div>
                            <div><label style={lbl}>Address line 2</label><input style={input} value={addForm.address.line2} onChange={(e) => setAddForm({ ...addForm, address: { ...addForm.address, line2: e.target.value } })} /></div>
                            <div><label style={lbl}>City</label><input style={input} value={addForm.address.city} onChange={(e) => setAddForm({ ...addForm, address: { ...addForm.address, city: e.target.value } })} /></div>
                            <div><label style={lbl}>State / Province</label><input style={input} value={addForm.address.state} onChange={(e) => setAddForm({ ...addForm, address: { ...addForm.address, state: e.target.value } })} /></div>
                            <div><label style={lbl}>Postal code</label><input style={input} value={addForm.address.postal_code} onChange={(e) => setAddForm({ ...addForm, address: { ...addForm.address, postal_code: e.target.value } })} /></div>
                            <div><label style={lbl}>Country</label><input style={input} value={addForm.address.country} onChange={(e) => setAddForm({ ...addForm, address: { ...addForm.address, country: e.target.value } })} /></div>
                        </div>
                        <label style={lbl}>Notes</label>
                        <textarea style={{ ...input, minHeight: 70 }} value={addForm.notes} onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })} />
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                            <button style={subtle} onClick={() => setShowAdd(false)}>Cancel</button>
                            <button style={primary} disabled={busy} onClick={createClient}>Create client</button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Client detail modal — tabbed (Details / Access / Documents) */}
            {detail && editForm && (
                <Modal
                    title={detail.client.client_name}
                    subtitle={`${detail.client.client_type === "organization" ? "Organization-backed client" : "Standalone client"} · ${detail.client.status}`}
                    onClose={() => setDetail(null)} wide>
                    <Tabs active={detailTab} onChange={setDetailTab} tabs={[
                        { key: "details", label: "Details" },
                        { key: "access", label: "Access", badge: (detail.users || []).length },
                        { key: "documents", label: "Documents", badge: (detail.documents || []).length },
                    ]} />

                    <Flash flash={flash} />

                    {detailTab === "details" && (
                        <div style={section}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
                                <div><label style={lbl}>Client name</label><input style={input} value={editForm.client_name} onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })} /></div>
                                <div><label style={lbl}>Status</label>
                                    <select style={input} value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                                        <option value="prospect">prospect</option><option value="active">active</option><option value="inactive">inactive</option>
                                    </select></div>
                                <div><label style={lbl}>Contact name</label><input style={input} value={editForm.primary_contact_name} onChange={(e) => setEditForm({ ...editForm, primary_contact_name: e.target.value })} /></div>
                                <div><label style={lbl}>Contact email</label><input style={input} value={editForm.primary_contact_email} onChange={(e) => setEditForm({ ...editForm, primary_contact_email: e.target.value })} /></div>
                                <div><label style={lbl}>Phone</label><input style={input} value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
                            </div>
                            <div style={{ fontWeight: 600, color: "#475569", fontSize: 13, marginTop: 8 }}>Address</div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
                                <div><label style={lbl}>Address line 1</label><input style={input} value={editForm.address.line1} onChange={(e) => setEditForm({ ...editForm, address: { ...editForm.address, line1: e.target.value } })} /></div>
                                <div><label style={lbl}>Address line 2</label><input style={input} value={editForm.address.line2} onChange={(e) => setEditForm({ ...editForm, address: { ...editForm.address, line2: e.target.value } })} /></div>
                                <div><label style={lbl}>City</label><input style={input} value={editForm.address.city} onChange={(e) => setEditForm({ ...editForm, address: { ...editForm.address, city: e.target.value } })} /></div>
                                <div><label style={lbl}>State / Province</label><input style={input} value={editForm.address.state} onChange={(e) => setEditForm({ ...editForm, address: { ...editForm.address, state: e.target.value } })} /></div>
                                <div><label style={lbl}>Postal code</label><input style={input} value={editForm.address.postal_code} onChange={(e) => setEditForm({ ...editForm, address: { ...editForm.address, postal_code: e.target.value } })} /></div>
                                <div><label style={lbl}>Country</label><input style={input} value={editForm.address.country} onChange={(e) => setEditForm({ ...editForm, address: { ...editForm.address, country: e.target.value } })} /></div>
                            </div>
                            <label style={lbl}>Notes</label>
                            <textarea style={{ ...input, minHeight: 70 }} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
                            <div style={{ display: "flex", justifyContent: "flex-end" }}><button style={primary} disabled={busy} onClick={saveEdit}>Save changes</button></div>

                            {detail.client.client_type === "organization" ? (
                                <div style={{ marginTop: 18, borderTop: "1px solid #e2e8f0", paddingTop: 12, fontSize: 12, color: "#64748b" }}>
                                    Organization-backed client — manage or remove the organization in the Organizations section. It cannot be deleted here.
                                </div>
                            ) : (
                                <div style={{ marginTop: 18, borderTop: "1px solid #fecaca", paddingTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
                                    <button style={{ ...subtle, background: "#fee2e2", color: "#991b1b" }} disabled={busy} onClick={() => deleteClient(detail.client.id)}>Delete client</button>
                                    <span style={{ fontSize: 12, color: "#94a3b8" }}>Permanently removes this client and all its proposals, quotes, documents and logins.</span>
                                </div>
                            )}
                        </div>
                    )}

                    {detailTab === "access" && (
                        <div style={section}>
                            {detail.client.client_type === "organization" ? (
                                <div style={{ fontSize: 13, color: "#64748b" }}>
                                    This is an <strong>organization-backed</strong> client. Its users access CloudGenomics through their organization and do <strong>not</strong> register separately for the client portal.
                                </div>
                            ) : (
                                <>
                                    <h4 style={{ marginTop: 0 }}>Invite a contact to register</h4>
                                    <div style={{ display: "flex", gap: 8, maxWidth: 520 }}>
                                        <input style={{ ...input, marginBottom: 0 }} placeholder="email@client.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
                                        <button style={primary} disabled={busy} onClick={sendInvite}>Send invite</button>
                                    </div>
                                    <h4>Client logins</h4>
                                    {(detail.users || []).length ? (
                                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                            <thead><tr><th style={th}>Email</th><th style={th}>Role</th><th style={th}>Status</th></tr></thead>
                                            <tbody>{detail.users.map((u) => (<tr key={u.id}><td style={td}>{u.email}</td><td style={td}>{u.role}</td><td style={td}>{u.status}</td></tr>))}</tbody>
                                        </table>
                                    ) : <div style={{ color: "#64748b" }}>No client logins yet.</div>}
                                    {(detail.invitations || []).filter((i) => i.status === "pending").length ? (
                                        <div style={{ marginTop: 12, fontSize: 13, color: "#64748b" }}>
                                            Pending invites: {detail.invitations.filter((i) => i.status === "pending").map((i) => i.invitee_email).join(", ")}
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </div>
                    )}

                    {detailTab === "documents" && (
                        <div style={section}>
                            <input type="file" onChange={(e) => uploadDoc(e.target.files[0])} disabled={busy} />
                            <div style={{ marginTop: 12 }}>
                                {(detail.documents || []).length ? detail.documents.map((d) => (
                                    <div key={d.id} style={{ padding: "8px 0", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between" }}>
                                        <a href="#!" onClick={(e) => { e.preventDefault(); downloadDoc(d.id); }}>{d.filename}</a>
                                        <span style={{ color: "#94a3b8", fontSize: 12 }}>{d.source} · {new Date(d.uploaded_at).toLocaleDateString()}</span>
                                    </div>
                                )) : <div style={{ color: "#64748b", marginTop: 8 }}>No documents.</div>}
                            </div>
                        </div>
                    )}
                </Modal>
            )}

            {/* Proposals section — its own modal, opened per-client from the list */}
            {proposalsClient && (
                <Modal title={`Proposals — ${proposalsClient.client_name}`} onClose={() => setProposalsClient(null)}>
                    <Flash flash={flash} />
                    <div style={{ display: "flex", gap: 8, marginBottom: 16, maxWidth: 560 }}>
                        <input style={{ ...input, marginBottom: 0 }} placeholder="New proposal name" value={newProposalName} onChange={(e) => setNewProposalName(e.target.value)} />
                        <button style={primary} disabled={busy} onClick={createProposal}>Create proposal</button>
                    </div>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead><tr><th style={th}>Name</th><th style={th}>Status</th><th style={th}>Comments</th><th style={th}></th></tr></thead>
                        <tbody>
                            {proposals.map((p) => (
                                <tr key={p.id}>
                                    <td style={td}>{p.name}</td>
                                    <td style={td}>{p.status}</td>
                                    <td style={td}>{p.comment_count}</td>
                                    <td style={td}>
                                        <button style={subtle} onClick={() => openProposal(p.id)}>Open</button>{" "}
                                        <button style={{ ...subtle, background: "#fee2e2", color: "#991b1b" }} onClick={() => deleteProposal(p.id)}>Delete</button>
                                    </td>
                                </tr>
                            ))}
                            {!proposals.length && <tr><td style={td} colSpan={4}>No proposals yet.</td></tr>}
                        </tbody>
                    </table>
                </Modal>
            )}

            {/* Proposal detail modal — tabbed (Details / Quotes / Comments / Attachments) */}
            {prop && propForm && (
                <Modal title={prop.proposal.name} subtitle={`Proposal · ${prop.proposal.status}${prop.proposal.finalized ? " · Finalized" : ""}${prop.proposal.shared_at ? " · Shared" : ""}`} onClose={() => setProp(null)}>
                    <Tabs active={propTab} onChange={setPropTab} tabs={[
                        { key: "details", label: "Details" },
                        { key: "quotes", label: "Quotes", badge: quotes.length },
                        { key: "documents", label: "Documents", badge: propDocs.length },
                        { key: "comments", label: "Comments", badge: (prop.comments || []).length },
                        { key: "attachments", label: "Attachments", badge: (prop.attachments || []).length },
                    ]} />

                    <Flash flash={flash} />

                    {propTab === "details" && (
                        <div style={section}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
                                <div><label style={lbl}>Name</label><input style={input} value={propForm.name} onChange={(e) => setPropForm({ ...propForm, name: e.target.value })} /></div>
                                <div><label style={lbl}>Status</label>
                                    <select style={input} value={propForm.status} onChange={(e) => setPropForm({ ...propForm, status: e.target.value })}>
                                        {["draft", "sent", "in_review", "approved", "released", "rejected", "completed", "cancelled"].map((s) => <option key={s} value={s}>{s === "released" ? "released (visible to client)" : s}</option>)}
                                    </select></div>
                                <div><label style={lbl}>Data type</label><input style={input} value={propForm.data_type} onChange={(e) => setPropForm({ ...propForm, data_type: e.target.value })} /></div>
                                <div><label style={lbl}>Sample count</label><input style={input} type="number" value={propForm.sample_count} onChange={(e) => setPropForm({ ...propForm, sample_count: e.target.value })} /></div>
                                <div><label style={lbl}>Reference genome</label><input style={input} value={propForm.reference_genome} onChange={(e) => setPropForm({ ...propForm, reference_genome: e.target.value })} /></div>
                                <div><label style={lbl}>Deadline</label><input style={input} type="date" value={propForm.deadline} onChange={(e) => setPropForm({ ...propForm, deadline: e.target.value })} /></div>
                            </div>
                            <label style={lbl}>Nature of work</label>
                            <RichTextEditor key={`now-${prop.proposal.id}`} value={propForm.nature_of_work}
                                onChange={(html) => setPropForm((pf) => ({ ...pf, nature_of_work: html }))}
                                placeholder="Describe the nature of work. You can paste formatted text from Word." />
                            <label style={lbl}>Description</label>
                            <RichTextEditor key={`desc-${prop.proposal.id}`} value={propForm.description}
                                onChange={(html) => setPropForm((pf) => ({ ...pf, description: html }))}
                                placeholder="Project description. Formatting (bold, lists, headings) is preserved." />
                            <label style={lbl}>Deliverables</label>
                            <RichTextEditor key={`deliv-${prop.proposal.id}`} value={propForm.deliverables}
                                onChange={(html) => setPropForm((pf) => ({ ...pf, deliverables: html }))}
                                placeholder="List of deliverables." />
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4, gap: 8, flexWrap: "wrap" }}>
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                    <button style={{ ...subtle, background: "#dcfce7", color: "#065f46" }} disabled={busy} onClick={finalizeProposal}>{prop.proposal.finalized ? "Re-finalize" : "Finalize proposal"}</button>
                                    <button style={{ ...subtle, background: "#dbeafe" }} disabled={busy} onClick={shareProposal}>Share proposal</button>
                                    <button style={subtle} disabled={busy} onClick={generateProposalPdf}>Generate / download PDF</button>
                                </div>
                                <button style={primary} disabled={busy} onClick={saveProposal}>Save proposal</button>
                            </div>
                        </div>
                    )}

                    {propTab === "quotes" && (
                        <div style={section}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                <h4 style={{ margin: 0 }}>Quotes</h4>
                                <button style={primary} disabled={busy} onClick={() => createQuote(prop.proposal.id)}>+ New quote</button>
                            </div>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead><tr><th style={th}>Quote #</th><th style={th}>Version</th><th style={th}>Status</th><th style={th}>Total</th><th style={th}></th></tr></thead>
                                <tbody>
                                    {quotes.map((qq) => (
                                        <tr key={qq.id}>
                                            <td style={td}>{qq.quote_number}</td>
                                            <td style={td}>v{qq.current_version}</td>
                                            <td style={td}>{qq.status}</td>
                                            <td style={td}>{qq.current ? `${qq.current.currency} ${Number(qq.current.total).toFixed(2)}` : "—"}</td>
                                            <td style={td}>
                                                <button style={subtle} onClick={() => openQuote(qq.id)}>Edit</button>{" "}
                                                <button style={{ ...subtle, background: "#fee2e2", color: "#991b1b" }} onClick={() => deleteQuote(qq.id)}>Delete</button>
                                            </td>
                                        </tr>
                                    ))}
                                    {!quotes.length && <tr><td style={td} colSpan={5}>No quotes yet.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {propTab === "documents" && (
                        <div style={section}>
                            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
                                <select style={{ ...input, marginBottom: 0, maxWidth: 420 }} value={attachResourceId} onChange={(e) => setAttachResourceId(e.target.value)}>
                                    <option value="">Attach a document from the Resources library…</option>
                                    {resourceLib.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
                                </select>
                                <button style={primary} disabled={docBusy} onClick={attachDocument}>Attach</button>
                            </div>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead><tr><th style={th}>Document</th><th style={th}>Type</th><th style={th}>Status</th><th style={th}></th></tr></thead>
                                <tbody>
                                    {propDocs.map((d) => (
                                        <tr key={d.id}>
                                            <td style={td}>{d.title}</td>
                                            <td style={td}>{d.doc_type.replace(/_/g, " ")}</td>
                                            <td style={td}>{d.status}{d.finalized_at ? ` · ${new Date(d.finalized_at).toLocaleDateString()}` : ""}</td>
                                            <td style={{ ...td, whiteSpace: "nowrap" }}>
                                                <button style={subtle} onClick={() => openDocument(d.id)}>Open / Fill</button>{" "}
                                                <button style={subtle} disabled={docBusy} onClick={() => generateDocPdf(d.id)}>PDF</button>{" "}
                                                <button style={{ ...subtle, background: "#fee2e2", color: "#991b1b" }} disabled={docBusy} onClick={() => deleteDocument(d.id)}>Delete</button>
                                            </td>
                                        </tr>
                                    ))}
                                    {!propDocs.length && <tr><td style={td} colSpan={4}>No documents attached. Attach one from the Resources library above.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {propTab === "comments" && (
                        <div style={section}>
                            <div style={{ maxHeight: 360, overflowY: "auto", marginBottom: 12 }}>
                                {(prop.comments || []).map((c) => (
                                    <div key={c.id} style={{ marginBottom: 12, padding: 12, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8 }}>
                                        <div style={{ fontSize: 12, color: c.author_type === "client" ? "#b45309" : "#2563eb", fontWeight: 600 }}>
                                            {c.author_type === "client" ? "Client" : "CloudGenomics"} · {new Date(c.created_at).toLocaleString()}
                                        </div>
                                        <div style={{ fontSize: 14, marginTop: 4 }}>{c.body}</div>
                                    </div>
                                ))}
                                {!(prop.comments || []).length && <div style={{ color: "#64748b" }}>No comments yet.</div>}
                            </div>
                            <textarea style={{ ...input, minHeight: 70 }} placeholder="Add a comment…" value={commentText} onChange={(e) => setCommentText(e.target.value)} />
                            <div style={{ display: "flex", justifyContent: "flex-end" }}><button style={primary} disabled={busy} onClick={addComment}>Post comment</button></div>
                        </div>
                    )}

                    {propTab === "attachments" && (
                        <div style={section}>
                            <input type="file" onChange={(e) => uploadProposalAttachment(e.target.files[0])} disabled={busy} />
                            <div style={{ marginTop: 12 }}>
                                {(prop.attachments || []).length ? prop.attachments.map((d) => (
                                    <div key={d.id} style={{ padding: "8px 0", borderTop: "1px solid #e2e8f0" }}>
                                        <a href="#!" onClick={(e) => { e.preventDefault(); downloadProposalAttachment(d.id); }}>{d.filename}</a>
                                    </div>
                                )) : <div style={{ color: "#64748b", marginTop: 8 }}>No attachments.</div>}
                            </div>
                        </div>
                    )}
                </Modal>
            )}

            {/* Document editor — fills a structured document (e.g. Clinical Validation Plan) */}
            {docEditor && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100 }}>
                    <div style={{ width: "92vw", height: "92vh", background: "#f8fafc", borderRadius: 12, padding: 24, overflow: "auto" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, position: "sticky", top: 0, background: "#f8fafc", paddingBottom: 8, zIndex: 1 }}>
                            <div>
                                <h3 style={{ margin: 0 }}>{docEditor.document.title}</h3>
                                <div style={{ fontSize: 12, color: "#64748b" }}>{docEditor.document.doc_type.replace(/_/g, " ")} · {docEditor.document.status}</div>
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                                <button style={primary} disabled={docBusy} onClick={() => saveDocument()}>Save</button>
                                <button style={{ ...subtle, background: "#dcfce7", color: "#065f46" }} disabled={docBusy} onClick={() => generateDocPdf(docEditor.document.id)}>Generate PDF</button>
                                <button style={{ ...subtle, background: "#dbeafe" }} disabled={docBusy} onClick={() => saveDocument("final")}>Save &amp; Finalize</button>
                                <button style={subtle} onClick={() => { setDocEditor(null); setFlash(null); }}>Close</button>
                            </div>
                        </div>
                        <Flash flash={flash} />
                        {docEditor.document.doc_type === "clinical_validation_plan" ? (
                            <ClinicalValidationPlanForm value={docEditor.data} onChange={(d) => setDocEditor({ ...docEditor, data: d })} readOnly={docEditor.document.status === "final"} />
                        ) : (
                            <div style={section}>This document type has no structured editor yet.</div>
                        )}
                    </div>
                </div>
            )}

            {/* Client Quotes section — all quotes across the client's proposals */}
            {quotesClient && (
                <Modal title={`Quotes — ${quotesClient.client_name}`} subtitle="All quotes across this client's proposals." onClose={() => setQuotesClient(null)}>
                    <Flash flash={flash} />
                    <div style={{ ...section, marginBottom: 16 }}>
                        <h4 style={{ marginTop: 0 }}>New quote</h4>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", maxWidth: 640 }}>
                            <select style={{ ...input, marginBottom: 0 }} value={newQuoteProposalId} onChange={(e) => setNewQuoteProposalId(e.target.value)}>
                                <option value="">Select a proposal…</option>
                                {proposals.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                            <button style={primary} disabled={busy} onClick={() => createQuote(newQuoteProposalId)}>Create quote</button>
                        </div>
                        {!proposals.length && <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>This client has no proposals yet — create one in the Proposals section first.</div>}
                    </div>
                    <div style={section}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead><tr><th style={th}>Quote #</th><th style={th}>Proposal</th><th style={th}>Version</th><th style={th}>Status</th><th style={th}>Total</th><th style={th}></th></tr></thead>
                            <tbody>
                                {clientQuotes.map((qq) => (
                                    <tr key={qq.id}>
                                        <td style={td}>{qq.quote_number}</td>
                                        <td style={td}>{qq.proposal_name}</td>
                                        <td style={td}>v{qq.current_version}</td>
                                        <td style={td}>{qq.status}</td>
                                        <td style={td}>{qq.current ? `${qq.current.currency} ${Number(qq.current.total).toFixed(2)}` : "—"}</td>
                                        <td style={td}>
                                            <button style={subtle} onClick={() => openQuote(qq.id)}>Open</button>{" "}
                                            <button style={{ ...subtle, background: "#fee2e2", color: "#991b1b" }} onClick={() => deleteQuote(qq.id)}>Delete</button>
                                        </td>
                                    </tr>
                                ))}
                                {!clientQuotes.length && <tr><td style={td} colSpan={6}>No quotes yet.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </Modal>
            )}

            {/* Quote editor modal — row-by-row line items + totals + versions + PDF */}
            {qe && (
                <Modal title={`${qe.quote.quote_number} · v${qe.quote.current_version} (${qe.quote.status})`} onClose={() => setQe(null)}>
                    <Flash flash={flash} />
                    <div style={{ marginBottom: 12 }}>
                        {qItems.map((it, i) => (
                            <div key={i} style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 12, background: "#fff" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                                    <span style={{ fontWeight: 700, color: "#475569" }}>Item #{i + 1}</span>
                                    <button title="Move up" style={{ ...subtle, padding: "2px 10px" }} disabled={i === 0} onClick={() => moveRow(i, -1)}>▲</button>
                                    <button title="Move down" style={{ ...subtle, padding: "2px 10px" }} disabled={i === qItems.length - 1} onClick={() => moveRow(i, 1)}>▼</button>
                                </div>
                                <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
                                    <div style={{ flex: "2 1 240px" }}><label style={lbl}>Item title</label><input style={{ ...input, marginBottom: 0 }} value={it.title} onChange={(e) => updateRow(i, "title", e.target.value)} placeholder="e.g. WGS alignment (30×)" /></div>
                                    <div style={{ width: 84 }}><label style={lbl}>Qty</label><input style={{ ...input, marginBottom: 0 }} type="number" value={it.quantity} onChange={(e) => updateRow(i, "quantity", e.target.value)} /></div>
                                    <div style={{ width: 96 }}><label style={lbl}>Unit</label><input style={{ ...input, marginBottom: 0 }} value={it.unit} onChange={(e) => updateRow(i, "unit", e.target.value)} /></div>
                                    <div style={{ width: 120 }}><label style={lbl}>Unit price</label><input style={{ ...input, marginBottom: 0 }} type="number" value={it.unit_price} onChange={(e) => updateRow(i, "unit_price", e.target.value)} /></div>
                                    <div style={{ width: 110 }}><label style={lbl}>Amount</label><div style={{ padding: "8px 0", fontWeight: 600 }}>{((Number(it.quantity) || 0) * (Number(it.unit_price) || 0)).toFixed(2)}</div></div>
                                    <div style={{ textAlign: "center" }}><label style={lbl}>Taxable</label><input type="checkbox" checked={it.taxable} onChange={(e) => updateRow(i, "taxable", e.target.checked)} /></div>
                                    <div><button style={{ ...subtle, background: "#fee2e2", color: "#991b1b" }} onClick={() => removeRow(i)}>Remove</button></div>
                                </div>
                                <label style={lbl}>Description</label>
                                <textarea style={{ ...input, marginBottom: 0, minHeight: 150 }} value={it.description} onChange={(e) => updateRow(i, "description", e.target.value)} placeholder="Detailed description of this line item (methods, deliverables, assumptions…)" />
                            </div>
                        ))}
                        {!qItems.length && <div style={{ color: "#64748b", marginBottom: 12 }}>No line items yet — add one below.</div>}
                    </div>
                    <button style={subtle} onClick={addRow}>+ Add item</button>

                    <div style={{ display: "flex", gap: 24, marginTop: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
                        <div style={{ flex: "1 1 320px" }}>
                            <label style={lbl}>Terms &amp; conditions</label>
                            <textarea style={{ ...input, minHeight: 100 }} value={qMeta.terms} onChange={(e) => setQMeta({ ...qMeta, terms: e.target.value })} />
                        </div>
                        <div style={{ width: 280 }}>
                            <div style={{ display: "flex", gap: 16 }}>
                                <div style={{ flex: 1 }}><label style={lbl}>Tax %</label><input style={input} type="number" value={qMeta.tax_rate} onChange={(e) => setQMeta({ ...qMeta, tax_rate: e.target.value })} /></div>
                                <div style={{ flex: 1 }}><label style={lbl}>Discount</label>
                                    <div style={{ display: "flex", gap: 6 }}>
                                        <input style={{ ...input, marginBottom: 0 }} type="number" value={qMeta.discount} onChange={(e) => setQMeta({ ...qMeta, discount: e.target.value })} />
                                        <select style={{ ...input, marginBottom: 0, width: 92 }} value={qMeta.discount_type} onChange={(e) => setQMeta({ ...qMeta, discount_type: e.target.value })}>
                                            <option value="amount">{qMeta.currency || "amt"}</option>
                                            <option value="percent">%</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            {(() => { const t = previewTotals(); return (
                                <div style={{ fontSize: 14, marginTop: 8 }}>
                                    <div>Subtotal: {t.subtotal.toFixed(2)}</div>
                                    <div>Discount{qMeta.discount_type === "percent" ? ` (${Number(qMeta.discount) || 0}%)` : ""}: -{t.discount.toFixed(2)}</div>
                                    <div>Tax ({Number(qMeta.tax_rate) || 0}%): {t.tax.toFixed(2)}</div>
                                    <div style={{ fontWeight: "bold" }}>Total: {t.total.toFixed(2)} {qMeta.currency}</div>
                                </div>); })()}
                        </div>
                    </div>

                    {qe.approval && (
                        <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8, fontSize: 13,
                            background: qe.approval.decision === "approved" ? "#ecfdf5" : qe.approval.decision === "rejected" ? "#fef2f2" : "#fffbeb",
                            border: "1px solid #e2e8f0" }}>
                            <strong>{String(qe.approval.decision).replace(/_/g, " ")}</strong> by {qe.approval.signer_name || qe.approval.signer_email || "client"} on {new Date(qe.approval.signed_at).toLocaleString()}{qe.approval.ip ? ` · IP ${qe.approval.ip}` : ""}
                            {qe.approval.comment ? <div>Comment: {qe.approval.comment}</div> : null}
                            {qe.approval.has_contract ? <div style={{ marginTop: 6 }}><button style={subtle} onClick={downloadContract}>Download signed contract</button></div> : null}
                        </div>
                    )}

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
                        <div style={{ fontSize: 12, color: "#64748b" }}>
                            Versions: {qe.versions.map((v) => `v${v.version}(${v.status})`).join(", ")}
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                            <button style={{ ...subtle, background: "#fee2e2", color: "#991b1b" }} disabled={busy} onClick={() => deleteQuote(qe.quote.id)}>Delete quote</button>
                            <button style={subtle} disabled={busy} onClick={generateQuotePdf}>Generate / download PDF</button>
                            <button style={{ ...subtle, background: "#dbeafe" }} disabled={busy} onClick={sendQuote}>Send to client</button>
                            <button style={primary} disabled={busy} onClick={saveQuote}>Save quote</button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

// All modals are a fixed 90vw × 90vh shell (fixed header, scrollable body) so switching
// tabs never resizes the modal.
function Modal({ title, subtitle, onClose, children }) {
    return (
        <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "90vw", height: "90vh", maxWidth: 1400, display: "flex", flexDirection: "column", boxShadow: "0 24px 60px rgba(2,6,23,0.35)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 28px", borderBottom: "1px solid #e2e8f0", flex: "0 0 auto" }}>
                    <div>
                        <h3 style={{ margin: 0, color: "#213a22" }}>{title}</h3>
                        {subtitle ? <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>{subtitle}</div> : null}
                    </div>
                    <button style={{ ...subtle, padding: "6px 12px" }} onClick={onClose}>✕</button>
                </div>
                <div style={{ padding: 28, overflowY: "auto", flex: "1 1 auto", minHeight: 0 }}>{children}</div>
            </div>
        </div>
    );
}

// In-modal success/error banner (feedback without a browser alert).
function Flash({ flash }) {
    if (!flash) return null;
    const err = flash.kind === "error";
    return (
        <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 14,
            background: err ? "#fef2f2" : "#ecfdf5", border: `1px solid ${err ? "#fca5a5" : "#6ee7b7"}`, color: err ? "#7f1d1d" : "#065f46" }}>
            {flash.text}
        </div>
    );
}

// Simple tab strip for sectioning busy modals.
function Tabs({ tabs, active, onChange }) {
    return (
        <div style={{ display: "flex", gap: 4, borderBottom: "2px solid #e2e8f0", marginBottom: 20 }}>
            {tabs.map((t) => (
                <button key={t.key} onClick={() => onChange(t.key)} style={{
                    padding: "10px 20px", border: "none", cursor: "pointer", fontWeight: 600, fontSize: 14,
                    background: "transparent", color: active === t.key ? "#213a22" : "#64748b",
                    borderBottom: active === t.key ? "3px solid #213a22" : "3px solid transparent", marginBottom: -2,
                }}>
                    {t.label}{t.badge ? ` (${t.badge})` : ""}
                </button>
            ))}
        </div>
    );
}
